import React from "react";
import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from "mobx";
import { ChatFileSchemaHandlers } from "models/OrganizationModels/OrganizationMetadataConfiguration";
import { BaseStore } from "stores/BaseStore";
import type { IRootStore } from "stores/RootStore";
import { AcxStore } from "stores/RootStore";
import { AuthStore } from "stores/AuthStore";
import { v4 as uuidv4 } from "uuid";
import { isEqual } from "lodash";
import type {
    AdvancedType,
    DataSourceType,
    ConcatFieldType,
    ConditionRuleType,
    CustomFieldType,
    FieldOptionsType,
    HasErrorType,
    KeyFieldType,
    MappedValueType,
    SetupType,
    SourceFieldType,
    TargetFieldOptions,
    TargetFieldType,
    GroupType,
    SelectedTargetFields,
} from "../types";
import {
    ConfigCreationSource,
    OrganizationMetadataFileType,
} from "../helpers/constants";
import { OrganizationMetadataFileTypeExtension } from "models/OrganizationModels/OrganizationMetadataFileTypeExtension";
import {
    checkIfStatementHasError,
    getExtension,
    isMeta1_5,
    runUpdatesWhenSourceChanges,
    updateStatementErrors,
} from "../helpers";
import { CampaignService } from "services/CampaignService";
import DataSourcesService from "services/DataSourcesService";
import {
    formatSourceFieldsFromData,
    getAdvancedFromData,
    getSetupFromData,
} from "../helpers/fromAPI";
import {
    formatCampaignIndicatorsForAPI,
    formatLanguageIndicatorsForAPI,
    formatMediaSegmentsForAPI,
    formatSetupForAPI,
    getUpdatedSourceFields,
} from "../helpers/toAPI";
import {
    defaultHasError,
    emptyConcatField,
    emptyCondition,
} from "../helpers/emptyStates";
import { DataHandlerStore } from "./DataHandlerStore";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { coreFields } from "services/MetadataService";
import ClassifierService from "services/ClassifierService";

@AcxStore
export class DataSourcesStore extends BaseStore {
    readonly dataHandlerStore: DataHandlerStore;
    readonly authStore: AuthStore;

    @observable selectedDataSource: DataSourceType | undefined;
    @observable dataSources: DataSourceType[] = [];
    @observable shouldApplyConditions: boolean = false;
    @observable shouldConcatenateFields: boolean = false;
    @observable sourceFields: SourceFieldType[] = [];
    @observable hasError: HasErrorType = defaultHasError;
    @observable conditionRules: ConditionRuleType[] = [];
    @observable concatFields: ConcatFieldType[] = [];
    @observable keyFields: KeyFieldType[] = [];
    @observable setup: SetupType;
    @observable targetFields: TargetFieldType[] = [];
    @observable selectedTargetFields: SelectedTargetFields = [];
    @observable customTargetFields: CustomFieldType[] = [];
    @observable mappedValues: MappedValueType[] = [];
    @observable advancedConfig: AdvancedType;
    @observable orgCampaignIds: FieldOptionsType[] = [];
    @observable hasSelectedDataSource: boolean = false;
    @observable isLoading = true;

    private campaignService = new CampaignService();
    private classifierService = new ClassifierService();
    private dataSourcesService = new DataSourcesService();

    constructor(private rootStore: IRootStore) {
        super("DataSourcesStore");
        makeObservable(this);

        this.authStore = rootStore.getStore(AuthStore);
        this.dataHandlerStore = rootStore.getStore(DataHandlerStore);

        // Check for unsaved changes
        reaction(
            () => [
                this.shouldApplyConditions,
                this.shouldConcatenateFields,
                this.sourceFields,
                this.conditionRules,
                this.concatFields,
                this.keyFields,
                this.setup,
                this.customTargetFields,
                this.mappedValues,
                this.advancedConfig,
            ],
            (
                [
                    shouldApplyConditions,
                    shouldConcatenateFields,
                    sourceFields,
                    conditionRules,
                    concatFields,
                    keyFields,
                    setup,
                    customTargetFields,
                    mappedValues,
                    advancedConfig,
                ],
                [
                    prevShouldApplyConditions,
                    prevShouldConcatenateFields,
                    prevSourceFields,
                    prevConditionRules,
                    prevConcatFields,
                    prevKeyFields,
                    prevSetup,
                    prevCustomTargetFields,
                    prevMappedValues,
                    prevAdvancedConfig,
                ],
            ) => {
                // If there's no selected data source or we there are unsaved changes, we don't need to check for changes
                if (
                    !this.hasSelectedDataSource ||
                    this.dataHandlerStore.hasUnsavedChanges ||
                    !this.dataHandlerStore.canEdit
                ) {
                    return;
                }
                if (
                    shouldApplyConditions !== prevShouldApplyConditions ||
                    shouldConcatenateFields !== prevShouldConcatenateFields ||
                    sourceFields !== prevSourceFields ||
                    conditionRules !== prevConditionRules ||
                    concatFields !== prevConcatFields ||
                    keyFields !== prevKeyFields ||
                    setup !== prevSetup ||
                    customTargetFields !== prevCustomTargetFields ||
                    mappedValues !== prevMappedValues ||
                    advancedConfig !== prevAdvancedConfig
                ) {
                    this.dataHandlerStore.setHasUnsavedChanges(true);
                }
            },
            { equals: isEqual },
        );
    }

    @computed
    get targetFieldOptions(): TargetFieldOptions {
        return {
            grouped: [
                {
                    options: [
                        {
                            label: "No selection",
                            value: "",
                            id: uuidv4(),
                        },
                    ],
                },
                {
                    label: "AMD Core Fields",
                    options: this.targetFields,
                },
                {
                    label: "Extended Metadata",
                    options: [
                        ...this.customTargetFields,
                        {
                            label: "Custom Extended Metadata Field Name",
                            value: "",
                            onClick: () =>
                                this.dataHandlerStore.setIsFieldModalOpen(true),
                            id: uuidv4(),
                            showTopBorder: true,
                            icon: <AddIcon />,
                        },
                        {
                            label: "Manage Extended Metadata Fields",
                            value: "",
                            onClick: () =>
                                this.dataHandlerStore.setIsManageFieldsModalOpen(
                                    true,
                                ),
                            id: uuidv4(),
                            icon: <EditIcon />,
                        },
                    ],
                },
            ],
            flattened: [...this.targetFields, ...this.customTargetFields],
        };
    }

    getUpdatedTargetFields = (): {
        targetFieldOptions: TargetFieldOptions;
        selectedTargetFields: SelectedTargetFields;
    } => {
        return {
            targetFieldOptions: {
                grouped: this.targetFieldOptions.grouped.map(
                    (targetField: GroupType) => {
                        if (!targetField.options) return targetField;

                        const updatedOptions = targetField.options.map(
                            (field) => ({
                                ...field,
                                isDisabled: this.selectedTargetFields.some(
                                    (selectedId) =>
                                        selectedId.targetFieldId === field.id,
                                ),
                            }),
                        );

                        return { ...targetField, options: updatedOptions };
                    },
                ),
                flattened: this.targetFieldOptions.flattened.map((field) => ({
                    ...field,
                })),
            },
            selectedTargetFields: this.selectedTargetFields,
        };
    };

    @action
    setAdvancedConfig = (config: AdvancedType) => {
        this.advancedConfig = config;
    };

    // keeps selected target fields up to date by adding newly selcted fields and removing unused fields
    @action
    updateSelectedTargetFields = ({
        targetFieldId,
        sourceFieldId,
        action,
    }: {
        targetFieldId?: string;
        sourceFieldId: string;
        action: "add" | "remove";
    }) => {
        let updatedFields = [...this.selectedTargetFields];

        if (action === "add" && targetFieldId) {
            updatedFields = updatedFields.filter(
                (f) => f.sourceFieldId !== sourceFieldId,
            );
            updatedFields.push({ targetFieldId, sourceFieldId });
        } else if (action === "remove") {
            updatedFields = updatedFields.filter(
                (f) => f.sourceFieldId !== sourceFieldId,
            );
        }

        this.selectedTargetFields = updatedFields;
    };

    @action
    hasInvalidConcatFields = () => {
        if (!this.shouldConcatenateFields) {
            return false;
        }
        let hasInvalidFields = false;
        const updatedFields = this.concatFields.map((field) => {
            field.hasError =
                !field.targetFieldName || field.fields.some((f) => !f.value);

            field.hasError && (hasInvalidFields = true);

            return field;
        });
        this.concatFields = updatedFields;
        return hasInvalidFields;
    };

    @action
    hasInvalidConditionRules = () => {
        let hasError = false;

        if (!this.shouldApplyConditions) {
            return hasError;
        }

        const updatedRules = this.conditionRules.map((rule) => {
            const hasTargetFieldError = !rule.targetFieldName;
            rule = updateStatementErrors(rule);
            const hasRuleError = checkIfStatementHasError(rule);

            rule.hasTargetFieldError = hasTargetFieldError;
            rule.hasError = hasRuleError;

            return rule;
        });
        this.conditionRules = updatedRules;

        return updatedRules.some(
            (rule) => rule.hasError || rule.hasTargetFieldError,
        );
    };

    @action
    checkIfSourceConfigIsValid = () => {
        const hasRulesError = this.hasInvalidConditionRules();
        const hasSourceFieldsError = this.checkIfSourceFieldsAreInvalid();
        const hasConcatFieldsError = this.hasInvalidConcatFields();
        const hasError =
            hasRulesError || hasSourceFieldsError || hasConcatFieldsError;

        return !hasError;
    };

    @action
    checkIfSourceFieldsAreInvalid = () => {
        const isMissingField = this.sourceFields.some(
            (field) => !field.alias || !field.path,
        );

        // Exclude aliases from core fields: 3, 4, 8, 9, 10
        const hasDuplicateAlias = this.sourceFields.some(
            (field) =>
                this.sourceFields.some(
                    (f) => f.alias === field.alias && f.id !== field.id,
                ) && !isMeta1_5(field.alias),
        );

        this.hasError.sourceFieldsMissing = isMissingField;
        this.hasError.sourceFieldsDuplicate = hasDuplicateAlias;
        return isMissingField || hasDuplicateAlias;
    };

    // Key field are only required for batch source types
    @action
    checkIfKeyFieldsAreValid = () => {
        if (this.setup.sourceType !== "batch") {
            return true;
        }

        // Filter out key fields that don't have a source field matching a source field's target
        // This can happen if the source field's target is changed after the key field was set
        const keyFields = this.keyFields.filter((f) =>
            this.sourceFields.some(
                (sf) => sf.targetFieldName === f.sourceFieldName,
            ),
        );

        const hasKeyFields = keyFields.some(
            (field) => field.sourceFieldName && field.targetFieldName,
        );

        // Check if any source field is missing a target field
        let hasSourceWithoutTarget = false;
        let hasCoreSourceandTarget = false;
        keyFields.forEach((kf) => {
            if (
                this.sourceFields.some(
                    (sf) =>
                        sf.alias === kf.sourceFieldName &&
                        !sf.targetFieldName.trim(),
                )
            ) {
                hasSourceWithoutTarget = true;
            }
            // Check if a source field's target is a core field and the key field's target is a core field too
            // If true and both fields match, then it's invalid
            if (
                this.sourceFields.some(
                    (sf) =>
                        sf.targetFieldName === kf.sourceFieldName &&
                        this.targetFields.some(
                            (st) => st.label === kf.targetFieldName,
                        ) &&
                        this.targetFields.some(
                            (st) => st.label === sf.targetFieldName,
                        ),
                )
            ) {
                hasCoreSourceandTarget = true;
            }
        });

        this.hasError.keyFieldsMissing = !hasKeyFields;
        this.hasError.keyFieldsSourceMissingTarget = hasSourceWithoutTarget;
        this.hasError.keyFieldsCoreSourceAndTarget = hasCoreSourceandTarget;

        return (
            hasKeyFields && !hasSourceWithoutTarget && !hasCoreSourceandTarget
        );
    };

    @action
    setSourceFields = (fields: SourceFieldType[]) => {
        const { updatedConcatFields, updatedConditionRules } =
            runUpdatesWhenSourceChanges(
                this.sourceFields,
                fields,
                this.conditionRules,
                this.concatFields,
            );

        this.conditionRules = updatedConditionRules;
        this.concatFields = updatedConcatFields;
        this.sourceFields = fields;
    };

    @action
    setMappedValues = (values: MappedValueType[]) => {
        this.mappedValues = values;
    };

    @action
    setCustomTargetFields = (fields: CustomFieldType[]) => {
        this.customTargetFields = fields;
        // make sure sourceFields don't have removed customTargetFields
        // Ensure field is either in core fields or customTargetFields
        this.sourceFields = this.sourceFields.map((sf) => {
            const hasExistingField = [...fields, ...this.targetFields].some(
                (field) => field.label === sf.targetFieldName,
            );

            return hasExistingField ? sf : { ...sf, targetFieldName: "" };
        });
    };

    @action
    setShouldApplyConditions = (applyConditions: boolean) => {
        this.shouldApplyConditions = applyConditions;

        if (this.checkIfSourceFieldsAreInvalid()) {
            this.shouldApplyConditions = false;
        } else if (applyConditions && this.conditionRules.length === 0) {
            this.conditionRules = [emptyCondition()];
        }
    };

    @action
    setConditionRules = (rules: ConditionRuleType[]) => {
        this.conditionRules = rules;
        if (this.conditionRules.some((rule) => rule.hasError)) {
            this.hasInvalidConditionRules();
        }
    };

    @action
    setConcatFields = (fields: ConcatFieldType[]) => {
        this.concatFields = fields;
        if (this.concatFields.some((field) => field.hasError)) {
            this.hasInvalidConcatFields();
        }
    };

    @action
    setShouldConcatenateFields = (concatenateFields: boolean) => {
        this.shouldConcatenateFields = concatenateFields;
        if (this.checkIfSourceFieldsAreInvalid()) {
            this.shouldConcatenateFields = false;
        } else if (concatenateFields && this.concatFields.length === 0) {
            this.concatFields = [emptyConcatField()];
        }
    };

    @action
    setKeyFields = (fields: KeyFieldType[]) => {
        this.keyFields = fields;
    };

    @action
    checkIfSetupIsValid = () => {
        this.hasError.setup = !this.setup.configurationName;
        return !this.hasError.setup;
    };

    @action
    setSetup = (setup: SetupType) => {
        this.setup = {
            ...setup,
            fileTypeExtension: setup.sourceType
                ? getExtension(setup.fileType, setup.sourceType)
                : "",
        };
        if (this.hasError.setup) {
            this.hasError.setup = !setup.configurationName;
        }
    };

    @action
    setSelectedDataSource = (datasource: DataSourceType) => {
        this.hasSelectedDataSource = false;
        this.selectedDataSource = datasource;
        const {
            sourceFields,
            concatFields,
            conditionRules,
            selectedTargetFields,
            mappedValues,
            keyFields,
            unresolvedCustomFields,
        } = formatSourceFieldsFromData(
            datasource,
            this.customTargetFields,
            this.targetFields,
        );
        this.sourceFields = sourceFields;
        this.concatFields = concatFields;
        this.conditionRules = conditionRules;
        this.selectedTargetFields = selectedTargetFields;
        this.mappedValues = mappedValues;
        this.keyFields = keyFields;
        // inject any fields the config references that aren't in the classifier list yet
        if (unresolvedCustomFields.length) {
            const existingIds = new Set(
                this.customTargetFields.map((f) => f.id),
            );
            const newFields = unresolvedCustomFields.filter(
                (f) => !existingIds.has(f.id),
            );
            if (newFields.length) {
                this.customTargetFields = [
                    ...this.customTargetFields,
                    ...newFields,
                ];
            }
        }
        this.shouldApplyConditions = conditionRules.length > 0;
        this.shouldConcatenateFields = concatFields.length > 0;
        this.setup = getSetupFromData(datasource);
        this.advancedConfig = getAdvancedFromData(datasource);

        // Reset errors - errors in condition rules and concat fields won't exist because
        // hasError will be undefined when setting values from data
        this.hasError = defaultHasError;

        setTimeout(() => {
            runInAction(() => {
                this.hasSelectedDataSource = true;
            });
        });
    };

    @action
    createDataSource = (selectedSource: string) => {
        const newDataSource: DataSourceType = {
            organizationId: this.authStore.activeOrgId || "",
            id: uuidv4(),
            metaDataFileType: OrganizationMetadataFileType[selectedSource],
            metaDataFileTypeExtension:
                OrganizationMetadataFileTypeExtension[
                    getExtension(selectedSource, "single")
                ],
            mediaFileTypes: undefined,
            isDebug: false,
            agentCodeRequired: false,
            sourceFieldConfigurations: [],
            metadataExtendedAssociation: {
                associationFields: [],
                matchesAllowed: 1,
                expiryHours: 24,
                retryIntervalHours: 1,
            },
            configCreationSource: ConfigCreationSource.UI,
        };
        this.setSelectedDataSource(newDataSource);
    };

    @action
    loadData = async () => {
        this.isLoading = true;
        this.hasSelectedDataSource = false;
        await this.setupAsyncTask(
            "Load Data Sources, Campaigns, and Metadata Fields",
            async () => {
                const [newDataSources, campaigns, extendedMetadataFields] =
                    await Promise.all([
                        this.dataSourcesService
                            .getOrganizationDataSources()
                            .catch(() => []),
                        this.campaignService.getCampaigns().catch(() => []),
                        this.classifierService
                            .getExtendedMetadataStringClassifiers()
                            .catch(() => []),
                    ]);

                runInAction(() => {
                    this.dataSources = newDataSources;

                    this.orgCampaignIds = campaigns.map((campaign) => ({
                        label: campaign.name,
                        value: campaign.id,
                    }));

                    this.customTargetFields = extendedMetadataFields
                        .map((field) => ({
                            label: field.name,
                            value: field.name,
                            id: field.id || uuidv4(),
                            isIndexed: field.isIndexed,
                        }))
                        .sort((a, b) => {
                            return a.label.localeCompare(b.label);
                        });

                    this.targetFields = coreFields.map((field) => ({
                        label: field.label,
                        value: field.value || 0,
                        id: uuidv4(),
                    }));
                });
            },
        );
        this.isLoading = false;
    };

    buildSaveRequest = (): DataSourceType | null => {
        const datasource = this.selectedDataSource;
        if (!datasource) return null;

        const updatedMediaSegments = formatMediaSegmentsForAPI(
            this.advancedConfig.mediaSegments,
            datasource.mediaSegments,
        );

        const updatedCampaignIndicators = formatCampaignIndicatorsForAPI(
            this.advancedConfig.campaignIndicatorDefinitions,
        );

        const updatedLanguageIndicators = formatLanguageIndicatorsForAPI(
            this.advancedConfig.languageIndicatorDefinitions,
        );

        const updatedSourceFields = getUpdatedSourceFields(
            this.sourceFields,
            this.shouldApplyConditions,
            this.conditionRules,
            this.shouldConcatenateFields,
            this.concatFields,
            this.mappedValues,
            this.customTargetFields,
            this.targetFields,
            this.selectedTargetFields,
        );

        const {
            metaDataFileType,
            metaDataFileTypeExtension,
            chatFileSchemaHandler,
            prefix,
            nameSpace,
            name,
            isDebug,
            agentCodeRequired,
            csvProperties,
        } = formatSetupForAPI(this.setup);

        return {
            organizationId: datasource.organizationId || "",
            id: datasource.id || "",
            metaDataFileType,
            metaDataFileTypeExtension,
            prefix,
            nameSpace,
            name,
            mediaFileTypes: undefined,
            isDebug,
            chatFileSchemaHandler: chatFileSchemaHandler
                ? ChatFileSchemaHandlers[chatFileSchemaHandler]
                : undefined,
            agentCodeRequired,
            csvProperties,
            sourceFieldConfigurations: updatedSourceFields,
            metadataExtendedAssociation: {
                associationFields: [
                    ...this.keyFields.filter(
                        (field) =>
                            field.sourceFieldName && field.targetFieldName,
                    ),
                ],
                // These three fields are not exposed in the UI; the API requires them but ignores their values for batch matching logic.
                matchesAllowed: 1,
                expiryHours: 24,
                retryIntervalHours: 1,
            },
            campaignIndicatorDefinitions: updatedCampaignIndicators,
            languageIndicatorDefinitions: updatedLanguageIndicators,
            mediaSegments: updatedMediaSegments,
            customDateFormatPatterns:
                this.advancedConfig.dateFormatPatterns.map(
                    (pattern) => pattern.pattern,
                ),
            configCreationSource: ConfigCreationSource.UI,
        };
    };

    shouldConfirmSave = (request: DataSourceType) => {
        const isExistingDataSource = this.dataSources.some(
            (dataSource) => dataSource.id === request.id,
        );
        if (!isExistingDataSource || !this.selectedDataSource) return false;

        return !isEqual(this.selectedDataSource, request);
    };

    @action
    saveConfiguration = async () => {
        const request = this.buildSaveRequest();
        if (!request || !this.dataHandlerStore.canEdit) return;

        try {
            await this.confirmAndSave(request);
        } catch (e) {
            console.error(e);
        }
    };

    @action
    confirmAndSave = async (request: DataSourceType) => {
        let updatedDataSources = [...this.dataSources];
        const isExistingDataSource = updatedDataSources.some(
            (dataSource) => dataSource.id === request.id,
        );
        if (isExistingDataSource) {
            updatedDataSources = updatedDataSources.map((dataSource) =>
                dataSource.id === request.id ? request : dataSource,
            );
        } else {
            updatedDataSources.push(request);
        }

        await this.dataSourcesService.setOrganizationDataSources(
            updatedDataSources,
        );
        this.loadData();
        this.dataHandlerStore.setActiveStep(0);
    };

    @action
    deleteDataSources = async () => {
        const selectedDataSources =
            this.dataHandlerStore.dataGridStore.selectedRows;

        // Check if at least one item selected is not a datasource before continuing
        if (
            !selectedDataSources.some((dataSource) => !dataSource.type) ||
            !this.dataHandlerStore.canEdit
        ) {
            return;
        }

        this.isLoading = true;
        const updatedDataSources = this.dataSources.filter(
            (dataSource) =>
                !selectedDataSources.some((ds) => ds.id === dataSource.id),
        );
        await this.dataSourcesService.setOrganizationDataSources(
            updatedDataSources,
        );
        this.dataHandlerStore.dataGridStore.clearSelected();
        await this.loadData();
    };
}
